import { isRecord } from '../../data/wpApi'
import type {
  ExternalMacro,
  FlowEdge,
  FlowGraphV1,
  FlowNode,
  FlowNodeData,
  FlowSnapshot,
  NodeMode,
  NodeType,
} from './types'

/** Decoded data fields in their original order; the order is visible in stored JSON. */
const DATA_FIELDS: Array<[keyof FlowNodeData, 'string' | 'number' | 'audio' | 'boolean']> = [
  ['phaseId', 'string'],
  ['groupId', 'string'],
  ['actionType', 'string'],
  ['delayMs', 'number'],
  ['pullDirection', 'string'],
  ['pullAnimation', 'string'],
  ['checkboxCount', 'number'],
  ['checkboxDelayMs', 'number'],
  ['waitDelayMs', 'number'],
  ['audioElementIndex', 'audio'],
  ['waitForAudio', 'audio'],
  ['getSource', 'string'],
  ['getRegister', 'string'],
  ['getLit', 'number'],
  ['getPhase', 'string'],
  ['setTarget', 'string'],
  ['setInput', 'string'],
  ['setLit', 'number'],
  ['mathTarget', 'string'],
  ['mathOp', 'string'],
  ['mathOperand', 'string'],
  ['mathLit', 'number'],
  ['condLeft', 'string'],
  ['condLeftLit', 'number'],
  ['condOp', 'string'],
  ['condRight', 'string'],
  ['condRightLit', 'number'],
  ['macroName', 'string'],
  ['macroRef', 'string'],
  ['inputCount', 'number'],
  ['outputCount', 'number'],
  ['mediaType', 'string'],
  ['displayMode', 'string'],
  ['mediaTypeRight', 'string'],
  ['mediaNameRight', 'string'],
  ['splitSwap', 'boolean'],
  ['trackId', 'string'],
]

const readNumber = (data: Record<string, unknown>, key: string) => {
  const v = data[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}
const readString = (data: Record<string, unknown>, key: string) => {
  const v = data[key]
  return typeof v === 'string' && v ? v : undefined
}

/**
 * Stored play-audio options are encoded in delayMs (see `encodeNodeDataForSave`):
 * delayMs >= 0 waits for audio element `delayMs`; delayMs < 0 plays element -(delayMs+1)
 * in the background. Explicit fields from newer data take precedence.
 */
const decodeAudioOptions = (data: Record<string, unknown>) => {
  const isPlayAudio = readString(data, 'actionType') === 'play-audio'
  const delay = readNumber(data, 'delayMs')
  const explicitIndex = readNumber(data, 'audioElementIndex') ?? readNumber(data, 'audioIndex')
  const explicitWait = data['waitForAudio']
  return {
    audioElementIndex:
      explicitIndex !== undefined
        ? explicitIndex
        : !isPlayAudio || delay === undefined
          ? undefined
          : delay < 0
            ? -(delay + 1)
            : delay,
    waitForAudio:
      typeof explicitWait === 'boolean'
        ? explicitWait
        : !isPlayAudio || delay === undefined
          ? undefined
          : delay >= 0,
  }
}

const decodeNodeData = (data: Record<string, unknown>): FlowNodeData => {
  const audio = decodeAudioOptions(data)
  const out: Record<string, unknown> = {}
  for (const [key, kind] of DATA_FIELDS) {
    if (kind === 'string') out[key] = readString(data, key)
    else if (kind === 'number') out[key] = readNumber(data, key)
    else if (kind === 'audio') out[key] = audio[key as keyof typeof audio]
    else out[key] = typeof data[key] === 'boolean' ? data[key] : undefined
  }
  return out as FlowNodeData
}

const readCoordinate = (item: Record<string, unknown>, axis: 'x' | 'y') => {
  const position = item.position
  const value =
    isRecord(position) && typeof position[axis] === 'number'
      ? (position[axis] as number)
      : typeof item[axis] === 'number'
        ? (item[axis] as number)
        : 0
  return Number.isFinite(value) ? value : 0
}

/**
 * Converts a stored v1 graph into editor nodes/edges. Returns null when any node or
 * edge lacks required fields, so callers can reject the whole document.
 */
export function coerceFlowGraphSnapshot(value: unknown): FlowSnapshot | null {
  if (!isRecord(value) || value.version !== 1) return null
  const rawNodes = value.nodes
  const rawEdges = value.edges
  if (!Array.isArray(rawNodes) || !Array.isArray(rawEdges)) return null

  const nodes: FlowNode[] = []
  for (const item of rawNodes) {
    if (!isRecord(item)) return null
    const id = typeof item.id === 'string' ? item.id : ''
    const type = typeof item.type === 'string' ? (item.type as NodeType) : null
    const label = typeof item.label === 'string' ? item.label : ''
    if (!id || !type || !label) return null
    nodes.push({
      id,
      type,
      label,
      locked: Boolean(item.locked),
      data: decodeNodeData(isRecord(item.data) ? item.data : {}),
      x: readCoordinate(item, 'x'),
      y: readCoordinate(item, 'y'),
    })
  }

  const edges: FlowEdge[] = []
  for (const item of rawEdges) {
    if (!isRecord(item)) return null
    const edge = {
      id: typeof item.id === 'string' ? item.id : '',
      fromNodeId: typeof item.fromNodeId === 'string' ? item.fromNodeId : '',
      fromPort: typeof item.fromPort === 'string' ? item.fromPort : '',
      toNodeId: typeof item.toNodeId === 'string' ? item.toNodeId : '',
      toPort: typeof item.toPort === 'string' ? item.toPort : '',
    }
    if (Object.values(edge).some((field) => !field)) return null
    edges.push(edge)
  }

  return { nodes, edges }
}

/**
 * The backend schema accepts only known data fields. Play-audio options are folded
 * into delayMs: >= 0 waits for element `delayMs`, < 0 plays element -(delayMs+1)
 * in the background.
 */
export function encodeNodeDataForSave(data: FlowNodeData): FlowNodeData {
  const next: FlowNodeData = { ...data }
  const legacy = data as Record<string, unknown>
  const audioElementIndex =
    typeof next.audioElementIndex === 'number' && Number.isFinite(next.audioElementIndex)
      ? next.audioElementIndex
      : undefined
  const legacyAudioIndex = typeof legacy.audioIndex === 'number' ? legacy.audioIndex : undefined
  const effectiveAudioIndex = audioElementIndex ?? legacyAudioIndex

  if (next.actionType === 'play-audio' && typeof effectiveAudioIndex === 'number') {
    const clampedIndex = Math.max(0, Math.floor(effectiveAudioIndex))
    next.delayMs = next.waitForAudio === false ? -(clampedIndex + 1) : clampedIndex
  }

  const record = next as Record<string, unknown>
  delete record.audioElementIndex
  delete record.audioIndex
  delete record.waitForAudio
  return next
}

const edgeToGraph = (edge: FlowEdge) => ({
  id: edge.id,
  fromNodeId: edge.fromNodeId,
  fromPort: edge.fromPort,
  toNodeId: edge.toNodeId,
  toPort: edge.toPort,
})

/** The graph exactly as it is saved to WordPress or handed to an overlay host. */
export function toFlowGraph(nodes: FlowNode[], edges: FlowEdge[]): FlowGraphV1 {
  return {
    version: 1,
    nodes: nodes.map((node) => ({
      id: node.id,
      type: node.type,
      label: node.label,
      locked: Boolean(node.locked),
      data: encodeNodeDataForSave(node.data) as Record<string, unknown>,
      position: { x: Math.round(node.x), y: Math.round(node.y) },
    })),
    edges: edges.map(edgeToGraph),
  }
}

/** Debug export: like the saved graph but with the editor's unencoded node data. */
export function toDebugGraph(nodes: FlowNode[], edges: FlowEdge[]) {
  return {
    version: 1,
    nodes: nodes.map((node) => ({
      id: node.id,
      type: node.type,
      label: node.label,
      locked: Boolean(node.locked),
      data: node.data,
      position: { x: Math.round(node.x), y: Math.round(node.y) },
    })),
    edges: edges.map(edgeToGraph),
  }
}

/** Selection templates keep node data as edited and omit the locked flag. */
export function toTemplateGraph(nodes: FlowNode[], edges: FlowEdge[]): FlowGraphV1 {
  return {
    version: 1,
    nodes: nodes.map((node) => ({
      id: node.id,
      type: node.type,
      label: node.label,
      data: JSON.parse(JSON.stringify(node.data)) as Record<string, unknown>,
      position: { x: node.x, y: node.y },
    })),
    edges: edges.map(edgeToGraph),
  }
}

/** A macro graph has no start node; the preview needs one wired to its macro-start. */
export function withPreviewStartNode(graph: FlowGraphV1, nodeMode: NodeMode): FlowGraphV1 {
  if (nodeMode !== 'macro' || graph.nodes.some((node) => node.type === 'start')) return graph
  const start = graph.nodes.find((node) => node.type === 'macro-start')
  if (!start) return graph
  return {
    version: 1,
    nodes: [
      {
        id: '__preview_start',
        type: 'start',
        label: 'Preview Start',
        locked: true,
        data: {},
        position: { x: (start.position?.x ?? 0) - 280, y: start.position?.y ?? 0 },
      },
      ...graph.nodes,
    ],
    edges: [
      {
        id: '__preview_start_edge',
        fromNodeId: '__preview_start',
        fromPort: 'next',
        toNodeId: start.id,
        toPort: 'in-0',
      },
      ...graph.edges,
    ],
  }
}

/** Name a host macro is called by: its macro-start name, else the macro's own name. */
export const externalMacroName = (macro: ExternalMacro) => {
  const start = macro.flowGraph.nodes.find((node) => node.type === 'macro-start')
  return typeof start?.data?.macroName === 'string' && start.data.macroName.trim()
    ? start.data.macroName.trim()
    : macro.name.trim()
}

/**
 * Appends host-supplied macros (far off-canvas, locked) so macro-calls resolve in the
 * preview. Local macros with the same name win.
 */
export function withExternalMacros(graph: FlowGraphV1, macros: ExternalMacro[]): FlowGraphV1 {
  const existingMacroNames = new Set(
    graph.nodes
      .filter((node) => node.type === 'macro-start')
      .map((node) => node.data?.macroName)
      .filter((name): name is string => typeof name === 'string' && name.trim().length > 0),
  )
  const externalNodes: FlowGraphV1['nodes'] = []
  const externalEdges: FlowGraphV1['edges'] = []

  for (const macro of macros) {
    const macroName = externalMacroName(macro)
    if (!macroName || existingMacroNames.has(macroName)) continue

    const prefix = `external-macro-${macro.id}__`
    externalNodes.push(
      ...macro.flowGraph.nodes.map((node) => ({
        ...node,
        id: `${prefix}${node.id}`,
        locked: true,
        data:
          node.type === 'macro-start'
            ? { ...(node.data ?? {}), macroName }
            : { ...(node.data ?? {}) },
        position: {
          x: (node.position?.x ?? 0) - 20000,
          y: (node.position?.y ?? 0) - 20000,
        },
      })),
    )
    externalEdges.push(
      ...macro.flowGraph.edges.map((edge) => ({
        ...edge,
        id: `${prefix}${edge.id}`,
        fromNodeId: `${prefix}${edge.fromNodeId}`,
        toNodeId: `${prefix}${edge.toNodeId}`,
      })),
    )
    existingMacroNames.add(macroName)
  }

  if (!externalNodes.length) return graph
  return {
    version: 1,
    nodes: [...graph.nodes, ...externalNodes],
    edges: [...graph.edges, ...externalEdges],
  }
}

/** Highest numeric suffix of ids like `node-12`, so new ids never collide with loaded ones. */
export function maxIdSuffix(ids: string[], prefix: 'node' | 'edge') {
  const pattern = new RegExp(`${prefix}-(\\d+)`)
  return ids
    .map((id) => (typeof id === 'string' ? pattern.exec(id)?.[1] : null))
    .map((x) => (x ? Number(x) : 0))
    .reduce((a, b) => (Number.isFinite(b) && b > a ? b : a), 0)
}
